import PropTypes from "prop-types";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { WITHDRAWAL_DEADLINE_PHRASE } from "lib/ni45110Copy";
import { WITHDRAWAL_NOTICE_EMAIL } from "lib/fortifyContact";
import { getProjectTitle } from "lib/investmentListHelpers";

export function CancelTransactionModal({
  open,
  onOpenChange,
  investment,
  cancellationNotes,
  onNotesChange,
  notesError,
  submitting,
  investorEmail,
  onConfirm,
  onClose,
}) {
  const projectTitle = investment ? getProjectTitle(investment) : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        if (!next) onClose();
        else onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md" showCloseButton={!submitting}>
        <DialogHeader>
          <DialogTitle>Cancel investment</DialogTitle>
          <DialogDescription>
            {projectTitle
              ? `Submit a cancellation request for "${projectTitle}".`
              : "Submit a cancellation request for this investment."}
            <span className="mt-2 block text-xs text-muted-foreground">
              NI 45-110 withdrawal must be {WITHDRAWAL_DEADLINE_PHRASE}. Notices may also be
              sent to{" "}
              <a
                href={`mailto:${WITHDRAWAL_NOTICE_EMAIL}`}
                className="font-medium text-accent hover:underline"
              >
                {WITHDRAWAL_NOTICE_EMAIL}
              </a>
              .
            </span>
            {investorEmail ? (
              <span className="mt-2 block text-xs">
                Request will be sent from <span className="font-medium">{investorEmail}</span>.
              </span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="cancellation-notes">
            Cancellation reason <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="cancellation-notes"
            value={cancellationNotes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Please describe why you want to cancel this investment..."
            rows={4}
            disabled={submitting}
            aria-invalid={Boolean(notesError)}
            aria-describedby={notesError ? "cancellation-notes-error" : undefined}
          />
          {notesError ? (
            <p id="cancellation-notes-error" className="text-sm text-destructive">
              {notesError}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={submitting}>
            {submitting ? "Submitting..." : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

CancelTransactionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  investment: PropTypes.object,
  cancellationNotes: PropTypes.string.isRequired,
  onNotesChange: PropTypes.func.isRequired,
  notesError: PropTypes.string.isRequired,
  submitting: PropTypes.bool.isRequired,
  investorEmail: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CancelTransactionModal.defaultProps = {
  investment: null,
};
